import React, { useEffect, useState } from "react";
import { useFinance } from "../../hooks/useFinance";
import { BankDetailProps } from "../../declarations/finance";
import Loader from "../common/Loader";
import { usePackage } from "../../hooks/usePackage";
import { setFeeValue } from "../../reducer/tourReducer";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../app/store";

interface FinanceProps {
  createdAt: string;
  createdBy: string;
  currency: string;
  country: string;
  tinCountry: string;
  paymentFrequency: string;
  tinNumber: string;
  updatedAt: string;
  vatNumber: string;
  vatRegistered: boolean;
}

const PaymentTaxDetails = ({
  transaction,
  loading,
}: {
  transaction: any;
  loading: any;
}) => {
  const {
    getFinance,
    applyfinanceSetting,
    createBankingDetails,
    getBankDetails,
    updateBankingDetails,
    updateApplyFinanceSetting,
  } = useFinance();
  const { calculateNetProfit, getPlatformFee } = usePackage();
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isTaxModalOpen, setIsTaxModalOpen] = useState(false);
  const [financeData, setFinanceData] = useState<FinanceProps>();
  const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] =
    useState(false);
  const [paymentMethod, setPaymentMethod] = useState("paypal");
  const [vatRegistered, setVatRegistered] = useState<string>("");
  const [tinNumber, setTinNumber] = useState<string>("");
  const [paymentFrequency, setPaymentFrequency] = useState<string>("");
  const [country, setCountry] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [financeLoading, setFinanceLoading] = useState(false);
  const [bankingDetailData, setBankingDetailData] = useState<BankDetailProps>(
    {}
  );
  const { feeValue } = useSelector((state: RootState) => state.tour);

  const dispatch = useDispatch();
  const [financeDetail, setFinanceDetail] = useState({
    vatNumber: "",
    tinNumber: "",
    vatRegistered: false,
    paymentFrequency: "",
    currency: "USD",
    country: "",
    tinCountry: "",
  });
  const [bankDetails, setBankDetails] = useState<BankDetailProps>({
    country: "",
    accountNumber: "",
    swift: "",
    accountType: "savings",
    beneficiaryName: "",
    bankCity: "",
    bankName: "",
    bankAddress: "",
    bankZip: "",
    paymentInstruction: "",
  });

  const openPaymentMethodModal = () => setIsPaymentMethodModalOpen(true);
  const closePaymentMethodModal = () => setIsPaymentMethodModalOpen(false);

  const handleSaveChanges = () => {
    // Logic to handle saving the payment details
    console.log({ paymentMethod, country });
    closePaymentModal();
  };

  const openPaymentModal = () => setIsPaymentModalOpen(true);
  const closePaymentModal = () => setIsPaymentModalOpen(false);

  const openTaxModal = () => setIsTaxModalOpen(true);
  const closeTaxModal = () => setIsTaxModalOpen(false);

  const refreshFinanceData = async () => {
    const updatedFinance = await getFinance();
    if (updatedFinance && updatedFinance.finance) {
      setFinanceData(updatedFinance.finance);
      setFinanceDetail({ ...updatedFinance.finance });
      setVatRegistered(updatedFinance.finance.vatRegistered ? "yes" : "no");
    }
  };

  const handleTaxDetail = async () => {
    setFinanceLoading(true);
    const payload = {
      ...financeDetail,
      vatRegistered: vatRegistered === "yes",
    };
    await applyfinanceSetting(payload);
    await refreshFinanceData();
    setFinanceLoading(false);
    setIsTaxModalOpen(false);
  };

  const handleEditTaxDetail = async () => {
    setFinanceLoading(true);
    const payload = {
      ...financeDetail,
      vatRegistered: vatRegistered === "yes",
    };
    await updateApplyFinanceSetting(payload);
    await refreshFinanceData();
    setFinanceLoading(false);
    setIsTaxModalOpen(false);
  };

  const handlePaymentSetting = async () => {
    setFinanceLoading(true);
    await applyfinanceSetting(financeDetail);
    await refreshFinanceData();
    setFinanceLoading(false);
    setIsPaymentModalOpen(false);
  };

  const handleEditPaymentSetting = async () => {
    setFinanceLoading(true);
    await updateApplyFinanceSetting(financeDetail);
    await refreshFinanceData();
    setFinanceLoading(false);
    setIsPaymentModalOpen(false);
  };

  const handleBankDetails = async () => {
    setFinanceLoading(true);
    try {
      const response = await createBankingDetails(bankDetails);
      if (response) {
        // Refresh bank details after successful creation
        const updatedResponse = await getBankDetails();
        if (updatedResponse && updatedResponse.bankingDetail) {
          setBankingDetailData(updatedResponse.bankingDetail);
        }
      }
    } catch (error) {
      console.error("Error creating bank details:", error);
    } finally {
      setFinanceLoading(false);
      setIsPaymentMethodModalOpen(false);
    }
  };

  const handleEditBankDetails = async () => {
    setFinanceLoading(true);
    try {
      const response = await updateBankingDetails(bankDetails);
      if (response) {
        // Refresh bank details after successful update
        const updatedResponse = await getBankDetails();
        if (updatedResponse && updatedResponse.bankingDetail) {
          setBankingDetailData(updatedResponse.bankingDetail);
        }
      }
    } catch (error) {
      console.error("Error updating bank details:", error);
    } finally {
      setFinanceLoading(false);
      setIsPaymentMethodModalOpen(false);
    }
  };

  useEffect(() => {
    const getFinanceDetail = async () => {
      const response = await getFinance();
      if (response && response.finance) {
        setFinanceData(response.finance);
        setVatRegistered(response.finance.vatRegistered ? "yes" : "no");
        setFinanceDetail({
          vatNumber: response.finance.vatNumber || "",
          tinNumber: response.finance.tinNumber || "",
          vatRegistered: response.finance.vatRegistered,
          paymentFrequency: response.finance.paymentFrequency || "",
          currency: response.finance.currency || "USD",
          country: response.finance.country || "",
          tinCountry: response.finance.tinCountry || "",
        });
      }
    };
    getFinanceDetail();
  }, []);

  useEffect(() => {
    const getBankDetailData = async () => {
      setFinanceLoading(true);
      const response = await getBankDetails();
      setFinanceLoading(false);
      if (response && response.bankingDetail) {
        setBankingDetailData(response.bankingDetail);
        setBankDetails({
          country: response.bankingDetail.country || "",
          accountNumber: response.bankingDetail.accountNumber || "",
          swift: response.bankingDetail.swift || "",
          accountType: response.bankingDetail.accountType || "",
          beneficiaryName: response.bankingDetail.beneficiaryName || "",
          bankCity: response.bankingDetail.bankCity || "",
          bankName: response.bankingDetail.bankName || "",
          bankAddress: response.bankingDetail.bankAddress || "",
          bankZip: response.bankingDetail.bankZip || "",
          paymentInstruction: response.bankingDetail.paymentInstruction || "",
        });
      }
    };
    getBankDetailData();
  }, []);

  useEffect(() => {
    const fetchPlatformFee = async () => {
      try {
        const response = await getPlatformFee({ setFinanceLoading });
        dispatch(setFeeValue(response.value));
      } catch (error) {
        console.error("Failed to fetch platform fee:", error);
      }
    };
    fetchPlatformFee();
  }, []);

  console.log("resps", financeData);
  console.log("bankdetails", bankingDetailData);

  return (
    <div>
      {financeLoading ? (
        <div className="mt-14">
          <Loader loading={financeLoading} />
        </div>
      ) : (
        <div className="bg-gray-50 min-h-screen">
          <div className="space-y-6">
            {/* Tax Details Section */}
            <div className="mt-6 bg-white p-4 sm:p-6 rounded-lg shadow">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
                <h3 className="text-lg font-semibold">Tax details</h3>
                <button
                  className="px-3 py-1 border border-blue-500 text-blue-600 rounded hover:bg-blue-50"
                  onClick={openTaxModal}
                >
                  Edit
                </button>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-16 text-gray-700">
                <div className="space-y-2">
                  <p className="font-bold">VAT number</p>
                  <p className="font-bold">Country of issue</p>
                  <p className="font-bold">Tax identification Number(TIN)</p>
                  <p className="font-bold">TIN country of issue</p>
                </div>

                <div className="space-y-2">
                  <p
                    className={
                      financeData?.vatNumber ? "text-black" : "text-red-500"
                    }
                  >
                    {financeData?.vatNumber
                      ? financeData?.vatNumber
                      : "Not specified"}
                  </p>
                  <p
                    className={
                      financeData?.country ? "text-black" : "text-red-500"
                    }
                  >
                    {financeData?.country
                      ? financeData?.country
                      : "Not specified"}
                  </p>
                  <p
                    className={
                      financeData?.tinNumber ? "text-black" : "text-red-500"
                    }
                  >
                    {financeData?.tinNumber
                      ? financeData?.tinNumber
                      : "Not specified"}
                  </p>
                  <p
                    className={
                      financeData?.tinCountry ? "text-black" : "text-red-500"
                    }
                  >
                    {financeData?.tinCountry
                      ? financeData?.tinCountry
                      : "Not specified"}
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Method Section */}
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
                <h3 className="text-lg font-semibold">Payment method</h3>
                <button
                  className="px-3 py-1 border border-blue-500 text-blue-600 rounded hover:bg-blue-50"
                  onClick={openPaymentMethodModal}
                >
                  Edit
                </button>
              </div>
              <div className="space-y-2 text-gray-700">
                {bankingDetailData !== null && (
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-36">
                    <div className="space-y-2">
                      <p className="font-bold">Account Number</p>
                      <p className="font-bold">SWIFT/BIC Type</p>
                      <p className="font-bold">Account Type</p>
                      <p className="font-bold">Beneficiary Name</p>
                      <p className="font-bold">Bank city</p>
                      <p className="font-bold">Bank Name</p>
                      <p className="font-bold">Bank address</p>
                      <p className="font-bold">Bank zip</p>
                      <p className="font-bold">Payment Instruction</p>
                    </div>

                    <div className="space-y-2">
                      <p>{bankingDetailData.accountNumber}</p>
                      <p>{bankingDetailData.swift}</p>
                      <p>{bankingDetailData.accountType}</p>
                      <p>{bankingDetailData.beneficiaryName}</p>
                      <p>{bankingDetailData.bankCity}</p>
                      <p>{bankingDetailData.bankName}</p>
                      <p>{bankingDetailData.bankAddress}</p>
                      <p>{bankingDetailData.bankZip}</p>
                      <p>{bankingDetailData.paymentInstruction}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Settings Section */}
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-2">
                <h3 className="text-lg font-semibold text-gray-900">
                  Payment settings
                </h3>
                <button
                  onClick={openPaymentModal}
                  className="px-4 py-2 border border-blue-500 text-blue-600 rounded-md hover:bg-blue-50"
                >
                  Edit
                </button>
              </div>
              <div className="space-y-4 text-gray-800">
                {/* Payment Frequency Section */}
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-20">
                  <span className="block font-bold text-gray-700 mb-2">
                    Payment frequency
                  </span>
                  <div className="flex items-start">
                    {financeData?.paymentFrequency === "once_per_month" ? (
                      <div>
                        <label className="flex items-start space-x-2 sm:space-x-7 w-full sm:w-80">
                          <div>
                            <p className="font-medium">Once per month</p>
                            <p className="text-sm text-gray-600">
                              Payment is issued on the 5th of every month or the
                              first following business day
                            </p>
                          </div>
                        </label>
                      </div>
                    ) : (
                      <div>
                        <label className="flex items-start gap-2 sm:gap-10 w-full sm:w-96 pl-0 sm:pl-8">
                          <div>
                            <p className="font-medium">
                              Twice per month (2% additional commission)
                            </p>
                            <p className="text-sm text-gray-600">
                              Payment is issued on the 5th & 20th of every month
                              or the first following business day
                            </p>
                          </div>
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                {/* Commission Rate Section */}
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-32 items-start sm:items-center">
                  <span className="font-bold text-gray-700">
                    Commission rate
                  </span>
                  <span className="text-gray-900">{feeValue} %</span>
                </div>

                {/* Currency Section */}
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-32 items-start sm:items-center">
                  <span className="font-bold text-gray-700">Currency</span>
                  <div className="flex flex-col">
                    <span className="text-gray-900">U.S. Dollar ($)</span>
                    <span className="text-sm text-gray-400">
                      Currency you will be paid by GetYourGuide.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tax Details Modal */}
          {isTaxModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-md">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Edit tax details</h3>
                  <button
                    onClick={closeTaxModal}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 011.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
                <p className="mb-2 text-sm text-gray-700">
                  Are you VAT-registered?
                </p>
                <div className="flex space-x-6 mb-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="vat"
                      value="yes"
                      checked={vatRegistered === "yes"}
                      onChange={() => setVatRegistered("yes")}
                      className="mr-2"
                    />
                    <span className="text-gray-800">Yes</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="vat"
                      value="no"
                      checked={vatRegistered === "no"}
                      onChange={() => setVatRegistered("no")}
                      className="mr-2"
                    />
                    <span className="text-gray-800">No</span>
                  </label>
                </div>

                {vatRegistered === "yes" && (
                  <div>
                    <select
                      className="w-full border p-2 rounded mb-4"
                      value={financeDetail.tinCountry}
                      onChange={(e) =>
                        setFinanceDetail((prev) => ({
                          ...prev,
                          tinCountry: e.target.value,
                        }))
                      }
                    >
                      <option value="Angola">Angola</option>
                      <option value="Algeria">Algeria</option>
                      <option value="Congo">Congo</option>
                      <option value="Denmark">Denmark</option>
                      <option value="Ethiopia">Ethiopia</option>
                      <option value="Greenland">Greenland</option>
                      <option value="Finland">Finland</option>
                      <option value="Hungary">Hungary</option>
                      <option value="Ireland">Ireland</option>
                      <option value="Jordan">Jordan</option>
                      <option value="Kenya">Kenya</option>
                      <option value="Libiya">Libiya</option>
                      <option value="Moroco">Moroco</option>
                      <option value="Nigeria">Nigeria</option>
                    </select>
                    <input
                      type="text"
                      value={financeDetail.tinNumber}
                      placeholder="Tax Identification Number"
                      className="w-full border p-2 rounded mb-4"
                      onChange={(e) =>
                        setFinanceDetail((prev) => ({
                          ...prev,
                          tinNumber: e.target.value,
                        }))
                      }
                    />
                    <input
                      type="text"
                      value={financeDetail.vatNumber}
                      placeholder="VAT Number"
                      className="w-full border p-2 rounded mb-4"
                      onChange={(e) =>
                        setFinanceDetail((prev) => ({
                          ...prev,
                          vatNumber: e.target.value,
                        }))
                      }
                    />
                    <select
                      className="w-full border p-2 rounded mb-4"
                      value={financeDetail.country}
                      onChange={(e) =>
                        setFinanceDetail((prev) => ({
                          ...prev,
                          country: e.target.value,
                        }))
                      }
                    >
                      <option value="Angola">Angola</option>
                      <option value="Algeria">Algeria</option>
                      <option value="Congo">Congo</option>
                      <option value="Denmark">Denmark</option>
                      <option value="Ethiopia">Ethiopia</option>
                      <option value="Greenland">Greenland</option>
                      <option value="Finland">Finland</option>
                      <option value="Hungary">Hungary</option>
                      <option value="Ireland">Ireland</option>
                      <option value="Jordan">Jordan</option>
                      <option value="Kenya">Kenya</option>
                      <option value="Libiya">Libiya</option>
                      <option value="Moroco">Moroco</option>
                      <option value="Nigeria">Nigeria</option>
                    </select>
                  </div>
                )}

                {(vatRegistered === "no" || vatRegistered === "") && (
                  <div>
                    <input
                      type="text"
                      value={vatNumber}
                      placeholder="VAT Number"
                      className="w-full border p-2 rounded mb-4"
                      onChange={(e) => setVatNumber(e.target.value)}
                    />
                    <select className="w-full border p-2 rounded mb-4">
                      <option value="Angola">Angola</option>
                      <option value="Algeria">Algeria</option>
                      <option value="Congo">Congo</option>
                      <option value="Denmark">Denmark</option>
                      <option value="Ethiopia">Ethiopia</option>
                      <option value="Greenland">Greenland</option>
                      <option value="Finland">Finland</option>
                      <option value="Hungary">Hungary</option>
                      <option value="Ireland">Ireland</option>
                      <option value="Jordan">Jordan</option>
                      <option value="Kenya">Kenya</option>
                      <option value="Libiya">Libiya</option>
                      <option value="Moroco">Moroco</option>
                      <option value="Nigeria">Nigeria</option>
                    </select>
                  </div>
                )}
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={closeTaxModal}
                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    onClick={
                      financeData ? handleEditTaxDetail : handleTaxDetail
                    }
                  >
                    {financeLoading ? "Please Wait..." : "Save changes"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Payment Method Modal */}
          {isPaymentMethodModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-lg h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-semibold mb-4">Payment details</h3>
                <p className="flex items-center justify-center text-2xl my-2">
                  Bank Transfer
                </p>
                <div>
                  <label className="block font-medium mb-1">
                    Account Number:
                  </label>
                  <input
                    type="text"
                    value={bankDetails.accountNumber}
                    onChange={(e) =>
                      setBankDetails((prev) => ({
                        ...prev,
                        accountNumber: e.target.value,
                      }))
                    }
                    placeholder="Enter your account number"
                    className="w-full p-2 border border-gray-300 rounded mb-4"
                  />
                  <label className="block font-medium mb-1">SWIFT/BIC</label>
                  <input
                    type="text"
                    value={bankDetails.swift}
                    onChange={(e) =>
                      setBankDetails((prev) => ({
                        ...prev,
                        swift: e.target.value,
                      }))
                    }
                    placeholder="Enter your SWIFT/BIC code"
                    className="w-full p-2 border border-gray-300 rounded mb-4"
                  />
                  <div>
                    <label className="block font-medium mb-1">
                      Account Type:
                    </label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded mb-4"
                      value={bankDetails.accountType}
                      onChange={(e) =>
                        setBankDetails((prev) => ({
                          ...prev,
                          accountType: e.target.value,
                        }))
                      }
                    >
                      <option value="savings">Savings</option>
                      <option value="checking">Checking</option>
                    </select>
                  </div>
                  <label className="block font-medium mb-1">
                    Beneficiay Name:
                  </label>
                  <input
                    type="text"
                    value={bankDetails.beneficiaryName}
                    onChange={(e) =>
                      setBankDetails((prev) => ({
                        ...prev,
                        beneficiaryName: e.target.value,
                      }))
                    }
                    placeholder="Enter beneficiary name"
                    className="w-full p-2 border border-gray-300 rounded mb-4"
                  />
                  <label className="block font-medium mb-1">Bank city:</label>
                  <input
                    type="text"
                    value={bankDetails.bankCity}
                    onChange={(e) =>
                      setBankDetails((prev) => ({
                        ...prev,
                        bankCity: e.target.value,
                      }))
                    }
                    placeholder="Enter bank city"
                    className="w-full p-2 border border-gray-300 rounded mb-4"
                  />
                  <label className="block font-medium mb-1">Bank Name:</label>
                  <input
                    type="text"
                    value={bankDetails.bankName}
                    onChange={(e) =>
                      setBankDetails((prev) => ({
                        ...prev,
                        bankName: e.target.value,
                      }))
                    }
                    placeholder="Enter bank name"
                    className="w-full p-2 border border-gray-300 rounded mb-4"
                  />
                  <label className="block font-medium mb-1">
                    Bank Address:
                  </label>
                  <input
                    type="text"
                    value={bankDetails.bankAddress}
                    onChange={(e) =>
                      setBankDetails((prev) => ({
                        ...prev,
                        bankAddress: e.target.value,
                      }))
                    }
                    placeholder="Enter bank address"
                    className="w-full p-2 border border-gray-300 rounded mb-4"
                  />
                  <label className="block font-medium mb-1">Bank zip:</label>
                  <input
                    type="text"
                    value={bankDetails.bankZip}
                    onChange={(e) =>
                      setBankDetails((prev) => ({
                        ...prev,
                        bankZip: e.target.value,
                      }))
                    }
                    placeholder="Enter bank zip code"
                    className="w-full p-2 border border-gray-300 rounded mb-4"
                  />
                  <label className="block font-medium mb-1">
                    Payment Instruction:
                  </label>
                  <span className="text-sm text-gray-500">
                    Optional, up to 35 characters
                  </span>
                  <input
                    type="text"
                    value={bankDetails.paymentInstruction}
                    onChange={(e) =>
                      setBankDetails((prev) => ({
                        ...prev,
                        paymentInstruction: e.target.value,
                      }))
                    }
                    placeholder="Enter payment instructions (optional)"
                    className="w-full p-2 border border-gray-300 rounded mb-4"
                  />
                </div>

                <div className="p-4 bg-yellow-100 text-yellow-700 rounded mb-4">
                  <p className="text-sm">
                    <strong>Payment information updates</strong> require
                    verification by the main account administrator. They need to
                    check their email inbox and follow the email instructions to
                    confirm these changes to enable future payments.
                  </p>
                </div>

                <div className="flex justify-end space-x-2">
                  <button
                    onClick={closePaymentMethodModal}
                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={
                      Object.keys(bankingDetailData).length > 0
                        ? handleEditBankDetails
                        : handleBankDetails
                    }
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    {financeLoading ? "Please wait..." : "Save changes"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Payment Settings Modal */}
          {isPaymentModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-md">
                <h3 className="text-lg font-semibold mb-4">
                  Edit payment settings
                </h3>
                <p className="font-medium mb-2">Payment frequency</p>
                <div className="space-y-2 mb-4">
                  <label className="flex items-start space-x-2">
                    <input
                      type="radio"
                      name="payment_frequency"
                      className="mt-1"
                      value="once_per_month"
                      checked={
                        financeDetail.paymentFrequency === "once_per_month"
                      }
                      onChange={() =>
                        setFinanceDetail((prev) => ({
                          ...prev,
                          paymentFrequency: "once_per_month",
                        }))
                      }
                    />
                    <div>
                      <span className="font-medium">Once per month</span>
                      <p className="text-gray-500 text-sm">
                        Payment is issued on the 5th of every month or the first
                        following business day.
                      </p>
                    </div>
                  </label>
                  <label className="flex items-start space-x-2">
                    <input
                      type="radio"
                      name="payment_frequency"
                      className="mt-1"
                      value="twice_per_month"
                      checked={
                        financeDetail.paymentFrequency === "twice_per_month"
                      }
                      onChange={() =>
                        setFinanceDetail((prev) => ({
                          ...prev,
                          paymentFrequency: "twice_per_month",
                        }))
                      }
                    />
                    <div>
                      <span className="font-medium">
                        Twice per month (2% additional commission)
                      </span>
                      <p className="text-gray-500 text-sm">
                        Payment is issued on the 5th & 20th of every month or
                        the first following business day.
                      </p>
                      <p className="text-gray-500 text-sm mt-1">
                        Selecting this option will increase your contractual
                        commission rate by 2% on future bookings.
                      </p>
                    </div>
                  </label>
                </div>
                <div className="flex justify-between items-center mb-4">
                  <span className="font-medium">Commission rate</span>
                  <span className="text-gray-700">{feeValue} %</span>
                </div>
                <div className="flex justify-between items-center mb-6">
                  <span className="font-medium">Currency</span>
                  <div className="flex flex-col w-64 pt-10">
                    <span className="text-gray-700">U.S. Dollar ($)</span>
                    <span className="text-sm text-gray-400">
                      You'll be paid in this currency. If you need to change
                      this for the next accounting period, contact us or your
                      destination manager.
                    </span>
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={closePaymentModal}
                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    onClick={
                      financeData
                        ? handleEditPaymentSetting
                        : handlePaymentSetting
                    }
                  >
                    {financeLoading ? "Please Wait..." : "Save changes"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentTaxDetails;
